import { formatDate } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';

import { FinancingHold } from 'src/app/api/models';
import { describeFinancingHold } from 'src/app/shared/financing-holds/financing-hold-text';

export class InvoiceNotification {
  id: string;
  color: string;
  content: string;
}

@Injectable()
export class InvoiceNotificationsService {

  private notifications$ = new ReplaySubject<InvoiceNotification[]>(1);
  endCustomerCreditLimit = false;
  endCustomerCreditInsuranceLimit = false;
  clientCreditLimit = false;
  clientOverdue = false;
  endCustomerOverdue = false;
  // The active holds that apply to the invoice (Invoice.financing_holds).
  // The invoice page sets them on every invoice it receives, whatever the
  // state, so the chips follow a placed or released hold.
  private financingHolds: FinancingHold[] = [];

  constructor(@Inject(LOCALE_ID) private locale: string) {
  }

  get(): Observable<InvoiceNotification[]> {
    return this.notifications$.asObservable();
  }

  reset(): void {
    this.endCustomerCreditInsuranceLimit = false;
    this.endCustomerCreditLimit = false;
    this.clientCreditLimit = false;
    this.clientOverdue = false;
    this.endCustomerOverdue = false;
    this.financingHolds = [];
    this.publish();
  }

  publish(): void {
    this.notifications$.next(this.boolsToNotifications());
  }

  setClientOverCreditLimit(b: boolean) {
    console.log("client over");
    this.clientCreditLimit = b;
    this.publish();
  }

  setEndCustomerOverCreditLimit(b: boolean) {
    this.endCustomerCreditLimit = b;
    this.publish();
  }

  setEndCustomerOverCreditInsuranceLimit(b: boolean) {
    this.endCustomerCreditInsuranceLimit = b;
    this.publish();
  }

  setEndCustomerOverdue(b: boolean) {
    this.endCustomerOverdue = b;
    this.publish();
  }

  setClientOverdue(b: boolean) {
    this.clientOverdue = b;
    this.publish();
  }

  /** One error-level chip per hold, ahead of every other notification. */
  setFinancingHolds(holds: FinancingHold[] | null | undefined): void {
    this.financingHolds = holds ?? [];
    this.publish();
  }

  private boolsToNotifications(): InvoiceNotification[] {
    const notifications = this.financingHolds.map((hold) => this.financingHoldNotification(hold));
    if (this.clientCreditLimit) {
      const n = new InvoiceNotification();
      n.color = "accent";
      n.content = "Client over credit limit";
      notifications.push(n);
    }
    if (this.endCustomerCreditLimit) {
      const n = new InvoiceNotification();
      n.color = "accent";
      n.content = "End Customer over credit limit";
      notifications.push(n);
    }
    if (this.endCustomerCreditInsuranceLimit) {
      const n = new InvoiceNotification();
      n.color = "primary";
      n.content = "End Customer over credit insurance limit";
      notifications.push(n);
    }
    if (this.clientOverdue) {
      const n = new InvoiceNotification();
      n.color = "primary";
      n.content = "Client has overdue invoices";
      notifications.push(n);
    }
    if (this.endCustomerOverdue) {
      const n = new InvoiceNotification();
      n.color = "primary";
      n.content = "End Customer has overdue invoices";
      notifications.push(n);
    }

    return notifications;
  }

  // The chip names what the hold is on, why, and who placed it when, so a
  // handler can tell holds apart and knows whom to ask.
  private financingHoldNotification(hold: FinancingHold): InvoiceNotification {
    const n = new InvoiceNotification();
    n.id = `financing-hold-${hold.id}`;
    n.color = 'warn';
    const placedOn = formatDate(hold.placed_at, 'd.M.yyyy', this.locale);
    n.content =
      `On financing hold — ${describeFinancingHold(hold)} ` +
      `(placed by ${hold.placed_by?.name ?? 'unknown'} on ${placedOn})`;
    return n;
  }
}
